/*
  PL - Kit color cards (Hair Tinsel kits)

  Shows one photo card per picked tinsel color at the top of the product
  gallery, so a customer building a two-color kit sees both colors together.

  How it works:
  - Runs only on the kit products listed in KITS below.
  - Loads the color photos from the component product (Perfect Hair Tinsel),
    where every color variant already has its own image. Nothing needs to be
    uploaded to the kits, and new tinsel colors appear automatically once they
    have a variant image on Perfect Hair Tinsel.
  - Any kit option whose values are tinsel colors gets a card (the snap clip
    option on the Clip-In Kit does not, since clip colors are not tinsel).
  - Cards update when either color is picked. The color photos already in the
    gallery are hidden on the desktop grid so the cards are not duplicated.
  - Desktop grid: the cards become the first tiles of the grid itself, next
    to the swatches the customer is clicking.
    Mobile: a compact "Your kit" summary sits directly under the swatches,
    because the gallery is scrolled out of view while choosing. The mobile
    gallery itself is left unchanged.
  - One set of cards per product section, even though the page has more than
    one <product-variants> (main form and sticky add-to-cart bar).

  Loaded on product templates from layout/theme.liquid.
*/
(function () {
  'use strict';

  if (window.__plKitColorCards) return;
  window.__plKitColorCards = true;

  // Kit product handle -> handle of the product that holds one photo per color.
  var KITS = {
    'hair-tinsel-starter-kit': 'perfect-hair-tinsel',
    'hair-tinsel-tape-in-kit': 'perfect-hair-tinsel',
    'hair-tinsel-clip-in-kit': 'perfect-hair-tinsel'
  };
  var MAX_CARDS = 2;
  var IMG_WIDTH = 900;

  var STYLE = [
    /* Desktop grid: cards are grid tiles, sized by the grid itself */
    '.pl-kit-card{position:relative;margin:0;overflow:hidden;border-radius:4px;background:rgb(var(--secondary-background,245,245,245));min-width:0}',
    '.pl-kit-card[hidden]{display:none}',
    '.pl-kit-card__img{display:block;width:100%;height:auto;aspect-ratio:5/6;object-fit:cover}',
    '.pl-kit-card__label{position:absolute;left:8px;bottom:8px;max-width:calc(100% - 16px);padding:4px 10px;border-radius:999px;background:#fff;color:#1a1a1a;font-size:13px;line-height:1.3;box-shadow:0 1px 3px rgba(0,0,0,.15)}',
    '.pl-kit-card__label strong{font-weight:600}',
    '.pl-kit-grid-hide{display:none !important}',
    /* Mobile "Your kit" summary under the swatches */
    '.pl-kit-summary{margin:0}',
    '.pl-kit-summary[hidden]{display:none}',
    '.pl-kit-summary__title{margin:0 0 8px;font-size:13px;font-weight:600;letter-spacing:.04em;text-transform:uppercase}',
    '.pl-kit-summary__list{display:grid;grid-template-columns:repeat(var(--pl-kit-cols,2),minmax(0,1fr));gap:8px}',
    '.pl-kit-summary .pl-kit-card{display:flex;align-items:center;gap:10px;padding:6px;border:1px solid rgba(var(--border-color,221,221,221));border-radius:6px;background:transparent}',
    '.pl-kit-summary .pl-kit-card[hidden]{display:none}',
    '.pl-kit-summary .pl-kit-card__img{flex:0 0 48px;width:48px;border-radius:4px}',
    '.pl-kit-summary .pl-kit-card__label{position:static;max-width:none;padding:0;background:none;color:inherit;box-shadow:none;font-size:13px;line-height:1.35}',
    '.pl-kit-summary .pl-kit-card__label strong{display:block;font-weight:600}',
    '@media (forced-colors: active){.pl-kit-card__label{border:1px solid CanvasText}}'
  ].join('');

  var colorMapCache = {};

  function injectStyle() {
    if (document.getElementById('pl-kit-cards-style')) return;
    var s = document.createElement('style');
    s.id = 'pl-kit-cards-style';
    s.textContent = STYLE;
    document.head.appendChild(s);
  }

  function stripTag(alt) {
    return String(alt || '').split('#')[0].trim();
  }

  function sizedSrc(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // Color name (lowercase) -> { src, alt } from the component product.
  function loadColorMap(handle) {
    if (colorMapCache[handle]) return colorMapCache[handle];
    var root = (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.rootUrlWithoutSlash) || '';
    colorMapCache[handle] = fetch(root + '/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (p) {
        var map = {};
        (p.variants || []).forEach(function (v) {
          var img = v.featured_image;
          if (!img || !img.src) return;
          map[String(v.option1).toLowerCase()] = { src: img.src, alt: stripTag(img.alt) || ('Hair tinsel in ' + v.option1) };
        });
        return map;
      });
    return colorMapCache[handle];
  }

  function loadKit(handle) {
    var root = (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.rootUrlWithoutSlash) || '';
    return fetch(root + '/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
  }

  // Options whose values are mostly tinsel colors, in option order.
  function tinselOptionPositions(kit, colorMap) {
    var positions = [];
    (kit.options || []).forEach(function (opt, i) {
      var values = opt.values || [];
      if (!values.length) return;
      var hits = values.filter(function (v) { return colorMap[String(v).toLowerCase()]; }).length;
      if (hits / values.length >= 0.5) positions.push(i + 1);
    });
    return positions.slice(0, MAX_CARDS);
  }

  function selectedValue(formId, position) {
    var name = 'option' + position;
    var checked = document.querySelector('input[name="' + name + '"][form="' + formId + '"]:checked');
    if (checked) return checked.value;
    var select = document.querySelector('select[name="' + name + '"][form="' + formId + '"]');
    return select ? select.value : '';
  }

  function buildFigure() {
    var fig = document.createElement('figure');
    fig.className = 'pl-kit-card';
    var img = document.createElement('img');
    img.className = 'pl-kit-card__img';
    img.width = 1600;
    img.height = 1920;
    img.decoding = 'async';
    img.loading = 'eager';
    var cap = document.createElement('figcaption');
    cap.className = 'pl-kit-card__label';
    fig.appendChild(img);
    fig.appendChild(cap);
    return fig;
  }

  function buildFigures(count) {
    var figs = [];
    for (var i = 0; i < count; i++) figs.push(buildFigure());
    return figs;
  }

  function fillFigure(fig, entry, value, index, width, hideSlotLabel) {
    if (!entry) { fig.hidden = true; return false; }
    fig.hidden = false;
    var img = fig.querySelector('img');
    var cap = fig.querySelector('figcaption');
    var src = sizedSrc(entry.src, width || IMG_WIDTH);
    if (img.getAttribute('src') !== src) {
      img.src = src;
      img.alt = entry.alt;
    }
    cap.innerHTML = '';
    var strong = document.createElement('strong');
    strong.textContent = 'Tinsel Color ' + (index + 1) + ':';
    // The summary sits right under the swatch headings that already say
    // "Tinsel Color 1/2", so the slot name is kept for screen readers only.
    if (hideSlotLabel) strong.className = 'visually-hidden';
    cap.appendChild(strong);
    cap.appendChild(document.createTextNode(' ' + value));
    return true;
  }

  function renderCards(state) {
    state.sets.forEach(function (set) {
      var anyShown = false;
      state.positions.forEach(function (pos, i) {
        var value = selectedValue(state.formId, pos);
        var entry = state.colorMap[String(value).toLowerCase()];
        if (fillFigure(set.figures[i], entry, value, i, set.imgWidth, set.hideSlotLabel)) anyShown = true;
      });
      if (set.wrap) set.wrap.hidden = !anyShown;
    });
  }

  // Hide the kit's own color photos (alt tagged "#Color_...") in the desktop
  // grid; the cards replace them. The mobile carousel is left untouched because
  // its cells are managed by Flickity.
  function hideGridColorMedia(state) {
    if (!state.grid) return;
    Array.prototype.forEach.call(state.grid.querySelectorAll('[data-media-id]'), function (item) {
      if (state.colorMediaIds[item.getAttribute('data-media-id')]) item.classList.add('pl-kit-grid-hide');
    });
  }

  var initializedSections = {};

  function init(picker) {
    var handle = picker.getAttribute('handle');
    var source = KITS[handle];
    if (!source) return;

    var section = picker.closest('[data-section-id]');
    var sectionId = section ? section.getAttribute('data-section-id') : null;
    if (!sectionId || initializedSections[sectionId]) return;
    initializedSections[sectionId] = true;

    var grid = document.getElementById('desktop-gallery-' + sectionId);

    // The main form's picker owns the selection; the sticky bar mirrors it.
    var mainPicker = section.querySelector('.product-form product-variants[handle="' + CSS.escape(handle) + '"]') || picker;

    Promise.all([loadKit(handle), loadColorMap(source)]).then(function (res) {
      var kit = res[0];
      var colorMap = res[1];
      var positions = tinselOptionPositions(kit, colorMap);
      if (!positions.length) return;

      injectStyle();
      var colorMediaIds = {};
      (kit.media || []).forEach(function (m) {
        if (m.alt && /#color_/i.test(m.alt)) colorMediaIds[String(m.id)] = true;
      });

      var sets = [];

      // Desktop grid: the cards are the first tiles of the grid.
      if (grid) {
        var gridFigs = buildFigures(positions.length);
        for (var i = gridFigs.length - 1; i >= 0; i--) grid.insertBefore(gridFigs[i], grid.firstChild);
        sets.push({ figures: gridFigs, wrap: null });
      }

      // Mobile (and desktop when there is no grid): a compact "Your kit" summary
      // directly under the swatches, where customers are choosing. When the
      // desktop grid exists it already shows the cards, so the summary is
      // mobile/tablet only.
      var summary = document.createElement('div');
      summary.className = 'pl-kit-summary' + (grid ? ' hidden-lap-and-up' : '');
      summary.setAttribute('role', 'group');
      summary.setAttribute('aria-labelledby', 'pl-kit-summary-title-' + sectionId);
      var title = document.createElement('p');
      title.className = 'pl-kit-summary__title';
      title.id = 'pl-kit-summary-title-' + sectionId;
      title.textContent = 'Your kit';
      var list = document.createElement('div');
      list.className = 'pl-kit-summary__list';
      list.style.setProperty('--pl-kit-cols', String(positions.length));
      var sumFigs = buildFigures(positions.length);
      sumFigs.forEach(function (f) { list.appendChild(f); });
      summary.appendChild(title);
      summary.appendChild(list);
      mainPicker.parentNode.insertBefore(summary, mainPicker.nextSibling);
      sets.push({ figures: sumFigs, wrap: summary, imgWidth: 160, hideSlotLabel: true });

      var state = {
        formId: mainPicker.getAttribute('form-id'),
        positions: positions,
        colorMap: colorMap,
        sets: sets,
        grid: grid,
        colorMediaIds: colorMediaIds
      };

      renderCards(state);
      hideGridColorMedia(state);

      document.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.name || !/^option\d$/.test(t.name)) return;
        renderCards(state);
      });
      document.addEventListener('variant:changed', function () {
        renderCards(state);
        hideGridColorMedia(state);
      });
      // Lazy media groups can add grid items after a color change.
      document.addEventListener('pl:media-groups-updated', function () { hideGridColorMedia(state); });
    }).catch(function (err) {
      if (window.console && console.warn) console.warn('[pl-kit-color-cards]', handle, err);
    });
  }

  function start() {
    Array.prototype.forEach.call(document.querySelectorAll('product-variants[handle]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
